/**
 * DiagramViewer - Read-only diagram viewer for embed views
 *
 * This is a lightweight, read-only version of DiagramBuilder designed for
 * public embed views. It receives all data as props and makes NO API calls,
 * ensuring it works without authentication.
 *
 * Key differences from DiagramBuilder:
 * - No API calls (all data passed via props)
 * - No editing capabilities
 * - No context menus
 * - No drag/drop
 * - Simplified state management
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  ControlButton,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { CustomNode, ZoneNode } from '../DiagramBuilder/CustomNode';
import type {
  Diagram,
  DiagramNode,
  DiagramEdge,
} from '../../../../shared/src/types/space.types';

// Custom node types for ReactFlow
const nodeTypes = {
  custom: CustomNode,
  zone: ZoneNode,
};

// Types for component data passed from embed API
export interface ComponentData {
  id: string;
  name: string;
  type: string;
  description?: string;
  confidentialityScore?: number;
  integrityScore?: number;
  availabilityScore?: number;
  risks?: RiskData[];
}

export interface RiskData {
  id: string;
  title: string;
  inherentRisk?: number;
  residualRisk?: number;
  mitigations?: MitigationData[];
}

export interface MitigationData {
  id: string;
  title: string;
  status: string;
}

interface DiagramViewerProps {
  /** The diagram data including nodes, edges, and canvas state */
  diagram: Diagram;
  /** Pre-fetched component data with risks (from embed API) */
  components?: ComponentData[];
  /** Height of the viewer container */
  height?: number | string;
  /** Whether to show the minimap */
  showMinimap?: boolean;
  /** Whether to show zoom controls */
  showControls?: boolean;
}

// Helper: Topological sort to ensure parent nodes come before children
function topologicalSortNodes<T extends { id: string; parentId?: string }>(
  nodes: T[]
): T[] {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const visited = new Set<string>();
  const result: T[] = [];

  function visit(nodeId: string) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (!node) return;

    // Visit parent first if exists
    if (node.parentId && nodeMap.has(node.parentId)) {
      visit(node.parentId);
    }

    result.push(node);
  }

  // Visit all nodes
  for (const node of nodes) {
    visit(node.id);
  }

  return result;
}

const DiagramViewerComponent: React.FC<DiagramViewerProps> = ({
  diagram,
  components = [],
  height = 500,
  showMinimap = false,
  showControls = true,
}) => {
  // Lock state for pan/zoom - default to locked
  const [isLocked, setIsLocked] = useState(true);

  const toggleLock = useCallback(() => {
    setIsLocked(prev => !prev);
  }, []);

  // Build component lookup map
  const componentsMap = useMemo(() => {
    return new Map(components.map(c => [c.id, c]));
  }, [components]);

  // Build risk score map for each component
  const risksMap = useMemo(() => {
    const map = new Map<string, { highestScore: number; count: number }>();

    for (const component of components) {
      if (component.risks && component.risks.length > 0) {
        const scores = component.risks.map(
          r => r.residualRisk ?? r.inherentRisk ?? 0
        );
        map.set(component.id, {
          highestScore: Math.max(...scores),
          count: component.risks.length,
        });
      }
    }

    return map;
  }, [components]);

  // Convert diagram data to ReactFlow nodes and edges
  const { nodes, edges, defaultViewport } = useMemo(() => {
    if (!diagram?.diagramData) {
      return { nodes: [], edges: [], defaultViewport: { x: 0, y: 0, zoom: 1 } };
    }

    const diagramData = diagram.diagramData;
    const loadedNodes: DiagramNode[] = diagramData.nodes || [];
    const loadedEdges: DiagramEdge[] = diagramData.edges || [];
    const canvasState = diagramData.canvasState;

    // Build a set of all node IDs for validation
    const allNodeIds = new Set(loadedNodes.map(n => n.id));

    // Clean up invalid parent references
    const cleanedNodes = loadedNodes.map(node => {
      if (node.parentId && !allNodeIds.has(node.parentId)) {
        return { ...node, parentId: undefined };
      }
      return node;
    });

    // Topological sort: parents must come before children
    const sortedNodes = topologicalSortNodes(cleanedNodes);

    // Convert to ReactFlow nodes
    const flowNodes: Node[] = sortedNodes.map(nodeData => {
      const component = componentsMap.get(nodeData.componentId);
      const riskInfo = risksMap.get(nodeData.componentId);
      const componentType = component?.type || nodeData.type || 'application';
      const isZoneType =
        componentType === 'zone' || componentType === 'security_zone';
      const reactFlowNodeType = isZoneType ? 'zone' : 'custom';

      return {
        id: nodeData.id,
        type: reactFlowNodeType,
        position: { x: nodeData.x, y: nodeData.y },
        data: {
          componentId: nodeData.componentId,
          label: component?.name || nodeData.id,
          type: componentType,
          // CIA scores
          confidentialityScore: component?.confidentialityScore,
          integrityScore: component?.integrityScore,
          availabilityScore: component?.availabilityScore,
          // Risk info
          highestRiskScore: riskInfo?.highestScore,
          riskCount: riskInfo?.count,
        },
        parentId: nodeData.parentId,
        extent: nodeData.parentId ? ('parent' as const) : undefined,
        // Dimensions
        width: isZoneType ? nodeData.width || 500 : nodeData.width || 150,
        height: isZoneType ? nodeData.height || 400 : nodeData.height || 80,
        style:
          nodeData.style ||
          (isZoneType
            ? { width: nodeData.width || 500, height: nodeData.height || 400 }
            : {
                width: nodeData.width,
                height: nodeData.height,
                backgroundColor: nodeData.color,
              }),
        // Read-only settings
        draggable: false,
        selectable: false,
        connectable: false,
      };
    });

    // Build valid node IDs (excluding zones which have no handles)
    const validNodeIds = new Set(
      flowNodes.filter(n => n.type !== 'zone').map(n => n.id)
    );

    // Convert to ReactFlow edges
    const flowEdges: Edge[] = loadedEdges
      .filter(edgeData => {
        if (!edgeData.sourceNodeId || !edgeData.targetNodeId) return false;
        if (!validNodeIds.has(edgeData.sourceNodeId)) return false;
        if (!validNodeIds.has(edgeData.targetNodeId)) return false;
        return true;
      })
      .map(edgeData => ({
        id: edgeData.id,
        source: edgeData.sourceNodeId,
        target: edgeData.targetNodeId,
        sourceHandle: edgeData.sourceHandle,
        targetHandle: edgeData.targetHandle,
        type: 'smoothstep', // Match DiagramBuilder edge style
        label: edgeData.label,
        data: { relationId: edgeData.relationId },
        // Read-only settings
        interactionWidth: 0,
        focusable: false,
        updatable: false,
      }));

    const viewport = canvasState
      ? {
          x: canvasState.offsetX,
          y: canvasState.offsetY,
          zoom: canvasState.zoom,
        }
      : { x: 0, y: 0, zoom: 1 };

    return { nodes: flowNodes, edges: flowEdges, defaultViewport: viewport };
  }, [diagram, componentsMap, risksMap]);

  return (
    <Box
      sx={{
        width: '100%',
        height,
        backgroundColor: 'grey.100',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        defaultViewport={defaultViewport}
        nodeTypes={nodeTypes}
        // Disable all editing
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        // Pan/zoom controlled by lock state
        panOnDrag={!isLocked}
        zoomOnScroll={!isLocked}
        zoomOnPinch={!isLocked}
        zoomOnDoubleClick={false}
        preventScrolling={true}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={15} size={1} />
        {showControls && (
          <Controls showInteractive={false}>
            <ControlButton
              onClick={toggleLock}
              title={isLocked ? 'Unlock pan & zoom' : 'Lock pan & zoom'}
            >
              {isLocked ? (
                <LockIcon fontSize="small" />
              ) : (
                <LockOpenIcon fontSize="small" />
              )}
            </ControlButton>
          </Controls>
        )}
        {showMinimap && <MiniMap nodeStrokeWidth={3} zoomable pannable />}
      </ReactFlow>
    </Box>
  );
};

/**
 * DiagramViewer with ReactFlow Provider wrapper
 */
export const DiagramViewer: React.FC<DiagramViewerProps> = props => {
  return (
    <ReactFlowProvider>
      <DiagramViewerComponent {...props} />
    </ReactFlowProvider>
  );
};

export default DiagramViewer;
